import { Fragment, useState, useEffect } from 'react';
import intl from 'react-intl-universal';
import KrillChat from 'krill';
import { useLatest } from 'ahooks';
import { useSnackbar } from 'notistack';
import BasicTable from 'components/BasicTable';
import { ConfirmModal } from 'shared/modals/confirmModal';
import { friendListColumns } from './FriendListColumns';
import TabBar from 'views/Contacts/components/TabBar';
import logger from 'utils/logger';
import { getCurrentUsername } from 'utils/privilege';
import AddFriendModal from './AddFriendModal';
import { ONLINE_STATUS, CONTACTS_STATUS } from './components/constants';

const log = logger.createLogger('Contacts');

const Contacts = () => {
  const context = KrillChat.Context.useKrillChatContext();
  const { friendList, recvFriendApplicationList, sentFriendApplicationList } =
    context;
  const [addFriendModalOpen, setAddFriendModalOpen] = useState(false);
  const [, setNewCountForReceived] = useState(0);
  const [friendsOnlineStatus, setFriendsOnlineStatus] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [tableData, setTableData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [
    filteredSentFriendApplicationList,
    setFilteredSentFriendApplicationList,
  ] = useState([]);

  const [deleteFriendConfirmModalOpen, setDeleteFriendConfirmModalOpen] =
    useState(false);
  const [deleteFriendUserID, setDeleteFriendUserID] = useState(null);
  const latestDeleteFriendUserID = useLatest(deleteFriendUserID);

  const { enqueueSnackbar } = useSnackbar();
  const currentUsername = getCurrentUsername();

  useEffect(() => {
    const getFriendsOnlineStatus = async () => {
      const friendUserIDs = friendList.map((friend) => {
        return friend.userID;
      });
      const onlineStatus = await KrillChat.API.getUsersOnlineStatus(
        friendUserIDs
      );
      setFriendsOnlineStatus(onlineStatus);
      setIsLoading(false);
    };
    getFriendsOnlineStatus();
  }, [friendList]);

  useEffect(() => {
    const unHandledApplication = recvFriendApplicationList.filter(
      (application) => application.handleResult === 0
    );
    setNewCountForReceived(unHandledApplication.length);
  }, []);

  // For FriendList
  useEffect(() => {
    if (!isLoading) {
      let tableData = [];
      let columns = [];

      // Handleresult -1 is cancelled, 0 is not handled, 1 is accepted
      // Important: fromUserID and toUserID
      // toUserID should always be the userID of the logged session
      // Array like {"fromUserID":"hxma","fromNickname":"Manger Yamato","fromFaceURL":"ic_avatar_01","fromGender":0,"toUserID":"hema","toNickname":"Manager E Jack","toFaceURL":"string","toGender":0,"handleResult":-1,"reqMsg":"","createTime":1711956370,"handlerUserID":"hema","handleMsg":"","handleTime":1711956370,"ex":"","attachedInfo":""}

      //! Filtering the data to ensure that the data is only for the current user
      const filteredRecvFriendApplicationList = recvFriendApplicationList
        .filter((application) => application.toUserID === currentUsername)
        .filter(
          (application, index, self) =>
            index ===
            self.findIndex((a) => a.fromUserID === application.fromUserID)
        );

      // ownerUserId should always be the userID of the logged session
      // Array like {"ownerUserID":"hema","userID":"hedb","remark":"","createTime":1682928907,"addSource":0,"operatorUserID":"hedb","nickname":"HEDB","faceURL":"","gender":0,"phoneNumber":"","birth":0,"email":"","ex":"","attachedInfo":""}

      const filteredFriendList = friendList.filter(
        (friend) => friend.ownerUserID === currentUsername
      );

      let friendTableData = [];
      friendTableData = filteredFriendList.map((friend) => {
        return {
          username: friend.nickname,
          userID: friend.userID,
          onlineStatus:
            friendsOnlineStatus.data.find(
              (status) => status.userID === friend.userID
            )?.status || ONLINE_STATUS.OFFLINE,
          operation: {
            userId: friend.userID,
            status: CONTACTS_STATUS.FRIEND,
          },
        };
      });

      let recvTableData = [];
      // Do not add if the user is already sent request
      recvTableData = filteredRecvFriendApplicationList
        .map((application, _) => {
          if (application.handleResult === 0) {
            // Check if the user is already in the friend list
            const isFriend = friendList.find(
              (friend) => friend.userID === application.fromUserID
            );
            if (isFriend) {
              return null;
            }

            return {
              username: application.fromNickname,
              userID: application.fromUserID,
              onlineStatus:
                friendsOnlineStatus.data.find(
                  (status) => status.userID === application.fromUserID
                )?.status || ONLINE_STATUS.OFFLINE,
              operation: {
                userId: application.fromUserID,
                status: CONTACTS_STATUS.RECEIVED,
              },
            };
          } else {
            return null;
          }
        })
        .filter((item) => item !== null);

      // This fromUserID should always be the actual user of the logged session
      // Array like {"fromUserID":"hema","fromNickname":"Manager E Jack","fromFaceURL":"string","fromGender":0,"toUserID":"hesb","toNickname":"HESB","toFaceURL":"","toGender":0,"handleResult":0,"reqMsg":"","createTime":1751531580,"handlerUserID":"","handleMsg":"","handleTime":0,"ex":"","attachedInfo":""}
      const filteredSentFriendApplicationList =
        sentFriendApplicationList.filter(
          (application) =>
            application.fromUserID === currentUsername &&
            application.handleResult === 0
        );
      setFilteredSentFriendApplicationList(filteredSentFriendApplicationList);

      // Adding the list for sent applications
      const sentApplicationTableData = filteredSentFriendApplicationList.map(
        (application) => {
          return {
            username: application.toNickname,
            userID: application.toUserID,
            onlineStatus: ONLINE_STATUS.OFFLINE, // It will be always offline since it is not a friend
            operation: {
              userId: application.toUserID,
              status: CONTACTS_STATUS.WAITING,
            },
          };
        }
      );

      tableData = [
        ...friendTableData,
        ...recvTableData,
        ...sentApplicationTableData,
      ];

      // Set the columns
      columns = friendListColumns(
        openChatWithUser,
        acceptApplication,
        rejectApplication,
        setDeleteFriendUserID,
        setDeleteFriendConfirmModalOpen
      );

      setColumns(columns);
      setTableData(tableData);
    }
  }, [
    isLoading,
    refreshTrigger,
    friendList,
    recvFriendApplicationList,
    sentFriendApplicationList,
    currentUsername,
  ]);

  const handleRefreshClick = () => {
    setRefreshTrigger(refreshTrigger + 1);
    enqueueSnackbar(intl.get('shared_table_data_reload_success'), {
      variant: 'success',
    });
  };

  const sendApplication = async (userID) => {
    KrillChat.API.addFriend(userID)
      .then(() => {
        enqueueSnackbar(
          intl.get('friends_snackbar_text_send_application_success'),
          {
            variant: 'success',
            autoHideDuration: 3000,
          }
        );
      })
      .catch((_) => {
        enqueueSnackbar(
          intl.get('friends_snackbar_text_send_application_error'),
          {
            variant: 'error',
            autoHideDuration: 3000,
          }
        );
      });
  };

  const openChatWithUser = async (userID) => {
    const sourceID = userID;
    const sessionType = 1;
    KrillChat.UI.Widget.Events.emit(
      KrillChat.Events.TOASSIGNCVE,
      sourceID,
      sessionType
    );
  };

  const deleteFriend = async (userID) => {
    KrillChat.API.deleteFriend(userID)
      .then(() => {
        enqueueSnackbar(
          intl.get('friends_snackbar_text_delete_friend_success'),
          {
            variant: 'success',
            autoHideDuration: 3000,
          }
        );
      })
      .catch((error) => {
        log.error('Error deleting friend', { error });
        enqueueSnackbar(intl.get('friends_snackbar_text_delete_friend_error'), {
          variant: 'error',
          autoHideDuration: 3000,
        });
      });
  };

  const acceptApplication = async (userID) => {
    KrillChat.API.acceptFriendApplication(userID)
      .then(() => {
        enqueueSnackbar(
          intl.get('friends_snackbar_text_accept_application_success'),
          {
            variant: 'success',
            autoHideDuration: 3000,
          }
        );
        setTimeout(() => {
          setRefreshTrigger(refreshTrigger + 1);
        }, 1000);
      })
      .catch((_) => {
        enqueueSnackbar(
          intl.get('friends_snackbar_text_accept_application_error'),
          {
            variant: 'error',
            autoHideDuration: 3000,
          }
        );
      });
  };

  const rejectApplication = async (userID) => {
    KrillChat.API.refuseFriendApplication(userID)
      .then(() => {
        enqueueSnackbar(
          intl.get('friends_snackbar_text_reject_application_success'),
          {
            variant: 'success',
            autoHideDuration: 3000,
          }
        );
        setTimeout(() => {
          setRefreshTrigger(refreshTrigger + 1);
        }, 1000);
      })
      .catch((_) => {
        enqueueSnackbar(
          intl.get('friends_snackbar_text_reject_application_error'),
          {
            variant: 'error',
            autoHideDuration: 3000,
          }
        );
      });
  };

  // Rewrking Single table with different actions depending on the user status
  // Need a modal to add a friend

  return (
    <Fragment>
      <BasicTable
        data={tableData}
        columns={columns}
        tableTitle={intl.get('dashboard_button_friends')}
        customToolbar={<TabBar setAddFriendModalOpen={setAddFriendModalOpen} />}
        enableRowSelection={false}
        darkMode={false}
        handleRefreshClick={handleRefreshClick}
      />

      <ConfirmModal
        isOpened={deleteFriendConfirmModalOpen}
        title={intl.get('friends_confirm_modal_title_delete_friend')}
        bodyText={latestDeleteFriendUserID.current}
        onConfirm={() => {
          deleteFriend(latestDeleteFriendUserID.current);
          setDeleteFriendConfirmModalOpen(false);
        }}
        onCancel={() => {
          setDeleteFriendUserID(null);
          setDeleteFriendConfirmModalOpen(false);
        }}
        confirmButtonColor="info"
        confirmButtonText={intl.get(
          'friends_confirm_modal_button_delete_friend_confirm'
        )}
        cancelButtonColor="error"
        cancelButtonText={intl.get(
          'friends_confirm_modal_button_delete_cancel'
        )}
      />

      {/* Add a friend modal */}
      <AddFriendModal
        isOpened={addFriendModalOpen}
        onClose={() => setAddFriendModalOpen(false)}
        sendApplication={sendApplication}
        friendList={friendList}
        sentFriendApplicationList={filteredSentFriendApplicationList}
        currentUsername={currentUsername}
      />
    </Fragment>
  );
};

export default Contacts;
